import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { SubmitHandler, useForm } from "react-hook-form"
import { useLazyQuery, useMutation } from "@apollo/client"
import esb from "elastic-builder"
import { Query, RULE_TYPE } from "lib/types/common"
import { useGetDiscountCodeRule } from "hooks/discounts"
import { v4 as uuidv4 } from "uuid"
import { useAppDispatch } from "lib/store"
import { Discount } from "lib/types"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import {
  DiscountCodeRule,
  DiscountCodeSearchHit,
  InputCreateOrUpdateDiscountCodeRule,
  LogicHolder
} from "lib/types/generated/graphql-types"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { transformLogicData } from "helpers/transformLogicData"
import { getLogicData } from "helpers/getLogicData"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { getJson } from "helpers/getJson"

import SEARCH_DISCOUNT_CODES from "graphql/queries/discount/SearchDiscountCodes"
import CREATE_OR_UPDATE_DISCOUNT_CODE_RULE from "graphql/mutations/discount/CreateOrUpdateDiscountCodeRule"

import { Activate, Deactivate, Divider } from "../Shared.styled"

import Box from "components/Ui/Box"
import PageSection from "components/Ui/Page/PageSection"
import PageHeader from "components/Ui/Page/PageHeader"
import PageSectionHeader from "components/Ui/Page/PageSectionHeader"
import LogicRules from "components/Discount/LogicRules"
import { BrinkLoader } from "components/Ui/BrinkLoader"
import { DiscountCodeTable } from "components/Discount/DiscountCode/DiscountCodeTable"
import GenerateDiscountCodes from "./GenerateDiscountCodes"
import UpdateDiscountCode from "./UpdateDiscountCode"
import CreateDiscountCode from "./CreateDiscountCode"
import DuplicateDiscountCodeRule from "./DuplicateDiscountCodeRule"
import ErrorMessage from "components/Ui/Messages/ErrorMessage"
import Status from "components/Ui/Status"
import DiscountRuleGeneralUpdateInput from "components/Discount/DiscountRule/DiscountRuleGeneralUpdateInput"
import { ActionDropdownButton } from "components/Ui/Buttons/ActionDropdownButton"
import DiscountRuleTimeLine from "components/Discount/TimeLine/DiscountRuleTimeLine"

import { ReactComponent as TagIcon } from "images/icons/tag.svg"
import { ReactComponent as TagsIcon } from "images/icons/tags.svg"
import { ReactComponent as CopyIcon } from "images/icons/copy.svg"
import { ReactComponent as OnIcon } from "images/icons/circle-play.svg"
import { ReactComponent as OffIcon } from "images/icons/circle-minus.svg"
import { ReactComponent as CodeIcon } from "images/icons/brackets-curly.svg"
import { ActionButtons, AddButton, StyledSearch } from "./UpdateDiscountCodeRule.styled"
import Popup from "../../../components/Ui/Popup"
import { Error404Page } from "../../404"
import { handleErrorMessages } from "helpers/errors"
import { PermissionGroups } from "../../../routes/constants"
import { WithAuth } from "../../../components/Auth"
import { TimeLineWrapper } from "../DiscountRules/CreateDiscountRule.styled"

export const UpdateDiscountCodeRule = () => {
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [rule, setRule] = useState<DiscountCodeRule>()
  const [discountCodes, setDiscountCodes] = useState<DiscountCodeSearchHit[]>([])
  const [discountCodesTotalHits, setDiscountCodesTotalHits] = useState<number>(0)
  const [editCode, setEditCode] = useState("")
  const [generateCodes, setGenerateCodes] = useState<boolean>(false)
  const [createDiscountCode, setCreateDiscountCode] = useState<boolean>(false)
  const [duplicateDiscountCodeRule, setDuplicateDiscountCodeRule] = useState<boolean>(false)
  const [showExclusiveWarning, setShowExclusiveWarning] = useState(false)

  const [logicState, setLogicState] = useState<Discount.LogicState>({
    [uuidv4()]: {
      conditions: [],
      outcomes: []
    }
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm<InputCreateOrUpdateDiscountCodeRule>()

  const isStackable = watch("isStackable")

  const { data: discountCodeRuleData, loading, refetch } = useGetDiscountCodeRule(id ?? "")

  useEffect(() => {
    if (discountCodeRuleData) {
      const { getDiscountCodeRule } = discountCodeRuleData
      setRule(getDiscountCodeRule)
      setValue("name", getDiscountCodeRule?.name)
      setValue("isActive", getDiscountCodeRule?.isActive)
      setValue("displayNames", getDiscountCodeRule?.displayNames)

      if (getDiscountCodeRule?.logicRules.length > 0) {
        setLogicState({})
        getDiscountCodeRule?.logicRules.forEach((logicRule: LogicHolder) => {
          setLogicState((prev: Discount.LogicState) => ({
            ...prev,
            ...(getLogicData(logicRule, getDiscountCodeRule) as Discount.LogicState)
          }))
        })
      }
    }
  }, [discountCodeRuleData])

  const referenceSearchQuery = esb.requestBodySearch().query(
    esb
      .boolQuery()
      .must(esb.matchQuery("discountCodeRuleId", id))
      .must([esb.queryStringQuery(`*${searchQuery}*`).analyzeWildcard(true)])
  )

  const query = referenceSearchQuery.toJSON() as Query
  const variables = {
    from: 0,
    size: 25,
    sort: [{ field: "updated", order: "ASC" }],
    query: JSON.stringify(query.query),
    trackTotalHits: true
  }

  const [searchDiscountCodes, { loading: codeLoading, error: codeError, refetch: refetchCodes }] =
    useLazyQuery(SEARCH_DISCOUNT_CODES, {
      variables
    })

  useEffect(() => {
    const delayDebounceFn = setTimeout(
      () => {
        searchDiscountCodes({
          variables
        }).then((response) => {
          setDiscountCodesTotalHits(response?.data?.searchDiscountCodes.total)
          setDiscountCodes(response?.data?.searchDiscountCodes.hits)
        })
      },
      searchQuery === "" ? 0 : 300
    )
    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery, generateCodes])

  const [updateDiscountRule, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_DISCOUNT_CODE_RULE,
    {
      onCompleted: () => {
        refetch()
        dispatch(
          alertActions.actions.createAlert({
            message: "Discount code rule successfully updated",
            type: "success"
          })
        )
        setShowExclusiveWarning(false)
      },
      onError: (error) => {
        handleErrorMessages(error)
        setShowExclusiveWarning(false)
      }
    }
  )

  const updateRule = (data: InputCreateOrUpdateDiscountCodeRule) => {
    const {
      name,
      isActive,
      isStackable,
      applyLast,
      isExclusive,
      sortOrder,
      validDateRange,
      displayNames
    } = data
    const transformedData = transformLogicData(logicState)
    updateDiscountRule({
      variables: {
        id,
        input: {
          name,
          displayNames,
          isActive: isActive ?? false,
          isStackable: isStackable ?? false,
          applyLast: applyLast ?? false,
          isExclusive: isExclusive ?? false,
          sortOrder,
          ...transformedData,
          ...(validDateRange?.from &&
            validDateRange.to && {
              validDateRange: {
                from: new Date(validDateRange.from),
                to: new Date(validDateRange.to)
              }
            })
        }
      }
    })
  }

  const onSubmit: SubmitHandler<InputCreateOrUpdateDiscountCodeRule> = (data) => {
    if (!rule?.isExclusive && data.isExclusive && rule?.isActive) {
      setShowExclusiveWarning(true)
      return
    }
    updateRule(data)
  }

  const forceSubmit: SubmitHandler<InputCreateOrUpdateDiscountCodeRule> = (data) => updateRule(data)

  const updateActive = (status: boolean) => {
    const { id: _id, ...rest } = rule ?? {}
    updateDiscountRule({
      variables: {
        id: rule?.id,
        input: { ...rest, isActive: status }
      }
    })
  }

  const watchedName = watch("name")
  const watchedValidFrom = watch("validDateRange.from")
  const watchedValidTo = watch("validDateRange.to")
  const watchedIsStackable = watch("isStackable")
  const watchedIsExclusive = watch("isExclusive")
  const watchedApplyLast = watch("applyLast")
  const watchedSortOrder = watch("sortOrder")

  useEffect(() => {
    if (isStackable === undefined) {
      setValue("isStackable", discountCodeRuleData?.getDiscountCodeRule?.isStackable)
      return
    }
    if (!isStackable) {
      setValue("isExclusive", false)
      setValue("applyLast", false)
    }
  }, [isStackable, rule, setValue])

  if (loading) return <BrinkLoader />
  if (!rule) return <Error404Page />

  return (
    <>
      {duplicateDiscountCodeRule && (
        <DuplicateDiscountCodeRule
          discountCodeRuleData={discountCodeRuleData.getDiscountCodeRule}
          logicState={logicState}
          setDuplicateDiscountCodeRule={setDuplicateDiscountCodeRule}
          setDiscountCodes={setDiscountCodes}
        />
      )}
      <Box padding="0" margin="0">
        <PageHeader
          title={
            <>
              {rule.name}
              {rule.isActive ? (
                <Status status="Active" />
              ) : (
                <Status status="Inactive" />
              )}
            </>
          }
          group="Discount rule"
          id={id}
          hasTabs
          goBackLinkUrl="/discounts/discount-rules"
          goBackLinkText="Back to discount rules"
        >
          <WithAuth requiredGroups={PermissionGroups.discounts.write}>
            <ActionDropdownButton
              title="Save"
              submitEvent={handleSubmit(onSubmit)}
              submitLoading={createLoading}
            >
              <li
                onClick={() => {
                  setDuplicateDiscountCodeRule(true)
                }}
              >
                <CopyIcon /> Duplicate
              </li>
              <li
                onClick={() => {
                  getJson(rule)
                }}
              >
                <CodeIcon /> Get rule JSON
              </li>
              {rule.isActive ? (
                <>
                  <Divider />
                  <Deactivate
                    onClick={() => {
                      updateActive(false)
                    }}
                  >
                    <OffIcon /> Deactivate rule
                  </Deactivate>
                </>
              ) : (
                <>
                  <Divider />
                  <Activate
                    onClick={() => {
                      updateActive(true)
                    }}
                  >
                    <OnIcon /> Activate rule
                  </Activate>
                </>
              )}
            </ActionDropdownButton>
          </WithAuth>
        </PageHeader>
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Logic rules</Tab>
            <Tab>Discount codes ({discountCodesTotalHits})</Tab>
          </TabList>
          <TabPanel>
            <form>
              {showExclusiveWarning && (
                <Popup
                  title={"Warning"}
                  subtitle={
                    "If you update an active discount code to be exclusive, any active customer sessions that already contain two or more of these codes may become invalid."
                  }
                  buttonText={"Ok, save"}
                  loading={createLoading}
                  handleOkClick={handleSubmit(forceSubmit)}
                  handleCloseClick={() => setShowExclusiveWarning(false)}
                />
              )}
              <DiscountRuleGeneralUpdateInput
                discountRule={rule}
                control={control}
                isStackable={isStackable}
                errors={errors}
                type={RULE_TYPE.EXTERNAL_RULE}
                setValue={setValue}
                watch={watch}
              />
            </form>
            <TimeLineWrapper>
              {(watchedValidTo || rule.validDateRange?.to) && (
                <DiscountRuleTimeLine
                  key={(watchedValidTo ?? rule.validDateRange?.to)?.toString()}
                  previewRule={{
                    name: watchedName ?? rule.name,
                    validFrom: watchedValidFrom ?? rule.validDateRange?.from,
                    validTo: watchedValidTo ?? rule.validDateRange?.to,
                    ruleType: "code",
                    sortOrder: watchedSortOrder ?? rule.sortOrder ?? 0,
                    isStackable: watchedIsStackable ?? rule.isStackable ?? false,
                    isExclusive: watchedIsExclusive ?? rule.isExclusive ?? false,
                    applyLast: watchedApplyLast ?? rule.applyLast ?? false,
                  }}
                />
              )}
            </TimeLineWrapper>
          </TabPanel>
          <TabPanel>
            <LogicRules state={logicState} setState={setLogicState} />
          </TabPanel>
          <TabPanel>
            <PageSection>
              <PageSectionHeader
                title="Discount codes"
                description="Discount codes connected to rule"
              />
              <WithAuth requiredGroups={PermissionGroups.discounts.write}>
                <ActionButtons>
                  <AddButton
                    type="button"
                    handleClick={() => {
                      dispatch(showEditSidebar())
                      setGenerateCodes(true)
                    }}
                  >
                    <TagsIcon /> Generate <div> discount </div> codes
                  </AddButton>
                  <AddButton
                    type="button"
                    handleClick={() => {
                      dispatch(showEditSidebar())
                      setCreateDiscountCode(true)
                    }}
                  >
                    <TagIcon /> Create <div> discount </div> code
                  </AddButton>
                </ActionButtons>
              </WithAuth>
            </PageSection>

            {createDiscountCode && (
              <CreateDiscountCode
                discountCodeRuleId={rule.id}
                setCreateDiscountCode={setCreateDiscountCode}
                setDiscountCodes={setDiscountCodes}
                discountCodes={discountCodes}
              />
            )}
            {editCode && (
              <UpdateDiscountCode
                discountCode={editCode}
                setEditId={setEditCode}
                setDiscountCodes={setDiscountCodes}
              />
            )}
            {generateCodes && (
              <GenerateDiscountCodes
                discountCodeRuleId={rule.id}
                setGenerateCodes={setGenerateCodes}
                refetch={refetchCodes}
              />
            )}
            <StyledSearch
              handleOnChange={(input) => setSearchQuery(input)}
              placeholder="Search by discount code name"
              loading={codeLoading}
              defaultValue={searchQuery}
            />
            <div>
              {codeError ? (
                <ErrorMessage
                  showRefreshButton
                  message={
                    <>
                      Error loading discount codes. Try refreshing the page, or contact{" "}
                      <a href="mailto:support@brinkcommerce.com">support</a>.
                    </>
                  }
                />
              ) : (
                <DiscountCodeTable
                  discountCodes={discountCodes}
                  setCodeToShow={setEditCode}
                  loading={loading}
                  error={codeError}
                />
              )}
            </div>
          </TabPanel>
        </Tabs>
      </Box>
    </>
  )
}
